import { PlusOutlined } from '@ant-design/icons'

import { createBrandApi } from '@eastcoal/api/content/brandOperation/createBrand'
import { queryBrandApi } from '@eastcoal/api/content/brandOperation/queryBrand'
import { updateBrandApi } from '@eastcoal/api/content/brandOperation/updateBrand'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { ObjectAcl } from '@eastcoal/common/enums/ObjectAcl'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'
import { mapNumberEnumOptions } from '@eastcoal/common/utils/enums'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { SubmitType } from '@eastcoal/formily/components/CrudForm/enums/SubmitType'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { InputNumber } from '@eastcoal/formily/components/InputNumber'
import { Select } from '@eastcoal/formily/components/Select'
import { UploadFile } from '@eastcoal/formily/components/UploadFile'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { PageName, PageNameTextMap } from '@eastcoal/shared/enums/PageName'
import { PagePosition } from '@eastcoal/shared/enums/PagePosition'

import { BrandFormExposedProps, BrandFormProps } from './interface'

const schema: BrandFormProps['schema'] = [
  {
    name: 'companyName',
    title: '企业名称',
    type: 'string',
    required: true,
    'x-component': Input,
    'x-decorator': FormItem
  },
  {
    name: 'ossIds',
    title: '图片',
    type: 'array',
    required: true,
    'x-component': UploadFile,
    'x-component-props': {
      multiple: false,
      maxCount: 1,
      objectAcl: ObjectAcl.PUBLIC_READ,
      listType: 'picture-card',
      accept: '.png, .jpg, .jpeg',
      children: <PlusOutlined />,
      maxFileSize: 20 * 1024 * 1024
    },
    'x-decorator': FormItem
  },
  {
    name: 'sort',
    title: '展示顺序',
    type: 'number',
    required: true,
    'x-component': InputNumber,
    'x-decorator': FormItem,
    'x-decorator-props': {
      tooltip:
        '序号仅限输入整数，数值越小越优先展示；序号相同时，优先展示最新编辑的'
    }
  },
  {
    name: 'navigateUrl',
    title: '跳转链接',
    type: 'string',
    'x-validator': [
      {
        format: 'url'
      }
    ],
    'x-component': Input,
    'x-decorator': FormItem
  },
  {
    name: 'pageName',
    title: '所属页面',
    type: 'number',
    enum: mapNumberEnumOptions(PageNameTextMap),
    'x-component': Select,
    'x-decorator': FormItem
  }
]

const readApi: BrandFormProps['readApi'] = ({ id }) => {
  return {
    api: async () => {
      const data = await queryBrandApi({ id })
      return {
        ...data,
        ossIds: [data.ossId]
      }
    },
    ready: id
  }
}

const updateApi: BrandFormProps['updateApi'] = (values, { submitType }) =>
  updateBrandApi({
    ...values,
    ossId: values.ossIds[0],
    status: submitType === SubmitType.SUBMIT ? 1 : 0
  })

const createApi: BrandFormProps['updateApi'] = (values, { submitType }) =>
  createBrandApi({
    ...values,
    ossId: values.ossIds[0],
    status: submitType === SubmitType.SUBMIT ? 1 : 0
  })

export const BrandForm = ({ mode }: BrandFormExposedProps) => {
  const { pageName = PageName.HOME } = useUrlParams()

  const navigate = useNavigate()
  const { message } = useFeedback()

  const handleSubmitSuccess = () => {
    message.success('操作成功')
    navigate(-1)
  }

  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        mode={mode}
        name='bannerForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        actions={[
          <BackButton key='backButton' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='sumbitButton'>发布</SubmitButton>
        ]}
        grid={{
          columns: 1
        }}
        initialValues={{
          pageName: Number.parseInt(pageName) as PageName,
          // 目前定死位置为页面底部
          pagePosition: PagePosition.BOTTOM
        }}
        layout={{
          wrapperWidth: 400
        }}
        onSuccess={handleSubmitSuccess}
      />
    </PageContainer>
  )
}
